/**
 * @file Entrypoint for surface page.
 */
import { configure as configureArvo } from '@padlet/arvo'
import { fetchJson } from '@padlet/fetch'
import { FetchOptionCredentials, FetchOptionMode } from '@padlet/fetch/lib/types'
import { setOauthToken as setJqueryAjaxOauthToken } from 'bits/ajax'
import { apolloProvider } from 'bits/apollo'
import { isStringTruthy } from 'bits/truthy'
import { getCable } from 'bits/cable'
import { observeConnectivity } from 'bits/connectivity'
import * as errorTracker from 'bits/error_tracker'
import { captureException, trackFailedIcons } from 'bits/error_tracker'
import fixMobileViewport from 'bits/fix_mobile_viewport'
import { isAppUsing } from 'bits/flip'
import window from 'bits/global'
import { reload } from 'bits/location'
import { captureOnlyWhitelistedClicks, initEventTracking, recordWallVisit } from 'bits/pepin'
import { setTimezoneCookie } from 'bits/timezone'
import { install as installNativeBridge } from 'native_bridge/install'
import { configureApiFetch } from 'surface/api_fetch'
import { watchHash } from 'surface/location'
import { trackPrinting } from 'surface/printing'
import { updateStoreFromSWMessage } from 'surface/service_worker_message'
import Vue, { CreateElement, VNode } from 'vue'
import VueApollo from 'vue-apollo'
import SurfaceApp from 'vuecomponents/surface_app.vue'
import store, { blankState } from 'vuexstore/surface/index'
import initialState from 'vuexstore/surface/initial_state'
import { RootState } from 'vuexstore/surface/types'
import 'styles/tailwind.css'

setTimezoneCookie()

Vue.use(VueApollo)

// Some data are too big/slow to be serialized so we don't put them in vueStartingState
// We will fetch them here. User will see the Padlet background loaded and then the content loaded after a while instead of waiting a long time with a blank page
async function fetchDataAndInitStore(): Promise<void> {
  // preloadedStartingState is preloaded (see show.html.erb), this should just get the preloaded data
  const startingStatePath = document.getElementById('starting-state-preload')!.getAttribute('href')
  let preloadedStartingState
  try {
    // for browsers that do not support preload, it will run a normal fetch request
    // NOTE: cors doesn't seem to work on Safari: https://stackoverflow.com/questions/52635660/can-link-rel-preload-be-made-to-work-with-fetch/63814972#63814972
    preloadedStartingState = await fetchJson(startingStatePath as string, {
      // preload send cookies with its request for resources from the same origin
      credentials: FetchOptionCredentials.include,
      // default mode is same-origin, preload uses no-cors
      mode: FetchOptionMode.noCors,
    })
  } catch (error) {
    // If the token expired, reload the page
    if (error.status === 410) {
      reload()
    } else {
      captureException(error, { context: { startingStatePath } })
    }
  }

  try {
    // We implement stale-while-revalidate with our service worker
    // when fresh data from server arrives and the cache is updated, it will notify clients
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        updateStoreFromSWMessage(event, preloadedStartingState.wall.id, store)
      })
    }

    const { arvoConfig, useArvo } = preloadedStartingState

    // Configure Arvo, ajax, ApiFetch
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search)
    const isBeta: boolean = isStringTruthy(urlParams.get('beta')) && preloadedStartingState.user.role == 'admin'
    arvoConfig.isBeta = isBeta
    setJqueryAjaxOauthToken(arvoConfig?.token?.oauthToken)
    arvoConfig.jsonApiVersion = 5
    await configureArvo(arvoConfig)
    configureApiFetch({ useArvo })

    // init store
    const startingState: RootState = {
      ...blankState(),
      ...initialState(),
      ...preloadedStartingState,
    }
    store.dispatch('initializeState', startingState)
    store.dispatch('realtime/connect', getCable())

    // error tracking
    errorTracker.init({
      vue: Vue,
      environment: store.state.environment,
      userId: store.state.user.id,
      locale: store.state.locale,
      release: store.state.release,
    })

    // event tracking
    initEventTracking({
      environment: store.state.environment,
      user: store.state.user,
      wall: store.state.wall,
      country: store.state.constants.currentCountryCode,
    })
    captureOnlyWhitelistedClicks()
    recordWallVisit()
  } catch (error) {
    captureException(error, { context: { startingStatePath } })
  }
}

const fetchDataAndInitStorePromise = fetchDataAndInitStore()

const domContentLoadedPromise = new Promise((resolve) => {
  document.addEventListener('DOMContentLoaded', (): void => {
    resolve()
  })
})

// These initializations depend on DOMContentLoaded only.
domContentLoadedPromise.then(() => {
  // Mobile browsers handle 100% height in ways we don't like. Fix that.
  fixMobileViewport()

  // Track when user tries to print document using Ctrl+P
  trackPrinting()

  // Perform actions based on location hash, e.g. open panels
  watchHash()

  // Inform state when the browser goes offline/online
  observeConnectivity((isOnline: boolean): void => {
    store.dispatch('changeConnectivity', { isOnline })
  })

  // install older native brige methods in the window object
  installNativeBridge(window, store)

  Vue.config.devtools = process.env.NODE_ENV !== 'production' || isAppUsing('debugFrontend')
  Vue.config.performance = process.env.NODE_ENV !== 'production' || isAppUsing('debugFrontend')

  if (process.env.NODE_ENV !== 'production' && isAppUsing('debugFrontend')) {
    console.log('Already debugging frontend in dev.')
  }
  Vue.config.ignoredElements = ['trix-editor']
})

Promise.all([fetchDataAndInitStorePromise, domContentLoadedPromise]).then(() => {
  // VueJS components
  window.app = new Vue({
    el: '#app',
    apolloProvider,
    store,
    render: (h: CreateElement): VNode => h(SurfaceApp),
    mounted(): void {
      // trackFailedIcons()
    },
  })
})
